import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Landmark,
  RefreshCw,
  RotateCcw,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api, fmtIDR } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import MarketplaceShippingReconciliation from "./MarketplaceShippingReconciliation";
import MarketplaceSettlement from "./MarketplaceSettlement";
import CustomerReturns from "./CustomerReturns";

const tabs = ["Overview", "Pengiriman", "Paket Kilat", "Retur & Gagal Kirim", "Marketplace & Cair"];
const cards = [
  ["total_marketplace_orders", "Total order marketplace"], ["matched", "Order matched"],
  ["cashier_without_file", "Kasir tanpa file"], ["marketplace_without_cashier", "Marketplace tanpa kasir"],
  ["linked_flash_orders", "Paket Kilat terhubung"], ["returns_in_transit", "Retur dalam perjalanan"],
  ["returns_ready", "Retur siap dibuka"], ["settlement_unmatched", "Settlement belum cocok"],
  ["unidentified_amount", "Nominal belum teridentifikasi"],
];

const QUEUE_STATUS_LABELS = {
  cashier_only: "Kasir tanpa File",
  duplicate_cashier: "Sales Ganda",
  marketplace_only: "Marketplace tanpa Kasir",
  multiple_packages: "Paket / Resi Ganda",
  needs_review: "Perlu Ditinjau",
};

const QUEUE_PAGE_SIZE = 10;

export default function ReconciliationControlCenter() {
  const [active, setActive] = useState("Overview");
  const [data, setData] = useState({ summary: {}, queue: [] });
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/reconciliation-control-center/overview");
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memuat Control Center");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="reconciliation-control-center-page">
      <PageHeader
        title="Reconciliation Control Center"
        subtitle="Pengiriman, Paket Kilat, retur, stok, dan pencairan dalam satu jejak."
      >
        <Button
          data-testid="reconciliation-control-center-refresh"
          onClick={load}
          variant="outline"
        >
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Muat Ulang
        </Button>
      </PageHeader>
      <div
        className="mb-6 flex flex-wrap gap-x-5 border-b border-taupe/20"
        data-testid="reconciliation-control-center-tabs"
      >
        {tabs.map((tab) => (
          <button
            className={`border-b-2 px-1 py-3 text-sm ${
              active === tab
                ? "border-cocoa text-cocoa"
                : "border-transparent text-wmuted"
            }`}
            data-testid={`reconciliation-tab-${tab}`}
            key={tab}
            onClick={() => setActive(tab)}
            type="button"
          >
            {tab}
          </button>
        ))}
      </div>
      {active === "Overview" && <Overview data={data} loading={loading} />}
      {active === "Pengiriman" && <MarketplaceShippingReconciliation />}
      {active === "Paket Kilat" && <FlashHub data={data} />}
      {active === "Retur & Gagal Kirim" && <CustomerReturns />}
      {active === "Marketplace & Cair" && <MarketplaceCashPanel />}
    </div>
  );
}

function Overview({ data, loading }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const queue = useMemo(() => data.queue || [], [data.queue]);
  const queueStatuses = useMemo(
    () => [...new Set(queue.map((row) => row.status).filter(Boolean))],
    [queue],
  );
  const filteredQueue = useMemo(() => {
    const keyword = search.trim().toLocaleLowerCase();

    return queue.filter((row) => {
      const matchesStatus = status === "all" || row.status === status;
      const values = [
        row.order_id,
        row.reason,
        row.pic,
        row.next_action,
        QUEUE_STATUS_LABELS[row.status],
      ];
      const matchesSearch = !keyword || values.some((value) => (
        String(value || "").toLocaleLowerCase().includes(keyword)
      ));

      return matchesStatus && matchesSearch;
    });
  }, [queue, search, status]);
  const totalPages = Math.max(1, Math.ceil(filteredQueue.length / QUEUE_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredQueue.slice(
    (currentPage - 1) * QUEUE_PAGE_SIZE,
    currentPage * QUEUE_PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [queue, search, status]);

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {cards.map(([key, label]) => (
          <Panel className="p-4" key={key}>
            <div className="text-xs text-wmuted">{label}</div>
            <div
              className="mt-1 text-2xl font-bold text-cocoa"
              data-testid={`reconciliation-summary-${key}`}
            >
              {loading
                ? "—"
                : key === "unidentified_amount"
                  ? fmtIDR(data.summary[key] || 0)
                  : data.summary[key] || 0}
            </div>
          </Panel>
        ))}
      </div>
      <Panel className="mt-6 p-4">
        <div className="mb-4 font-semibold text-cocoa">
          Action queue berdasarkan prioritas
        </div>
        <div
          className="mb-3 grid gap-2 sm:grid-cols-[minmax(220px,1fr)_200px_auto]"
          data-testid="reconciliation-action-queue-controls"
        >
          <div className="relative">
            <Label className="sr-only" htmlFor="reconciliation-action-queue-search">
              Cari action queue
            </Label>
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-wmuted" />
            <Input
              className="pl-9"
              data-testid="reconciliation-action-queue-search"
              id="reconciliation-action-queue-search"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Cari order, penyebab, atau PIC"
              value={search}
            />
          </div>
          <Select onValueChange={setStatus} value={status}>
            <SelectTrigger data-testid="reconciliation-action-queue-status-filter">
              <SelectValue placeholder="Semua status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua status</SelectItem>
              {queueStatuses.map((queueStatus) => (
                <SelectItem key={queueStatus} value={queueStatus}>
                  {QUEUE_STATUS_LABELS[queueStatus] || queueStatus}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="reconciliation-action-queue-reset-filter"
            onClick={resetFilters}
            size="icon"
            title="Reset filter"
            variant="outline"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
        <p
          className="mb-3 text-xs text-wmuted"
          data-testid="reconciliation-action-queue-result-count"
        >
          Menampilkan {pageRows.length} dari {filteredQueue.length} exception
        </p>
        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Penyebab</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Nominal</TableHead>
                <TableHead>PIC</TableHead>
                <TableHead>Tindakan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((row) => (
                <TableRow data-testid={`reconciliation-action-queue-row-${row.id}`} key={row.id}>
                  <TableCell className="font-mono text-xs">{row.order_id}</TableCell>
                  <TableCell>{row.reason}</TableCell>
                  <TableCell>{row.qty}</TableCell>
                  <TableCell>{fmtIDR(row.amount)}</TableCell>
                  <TableCell>{row.pic}</TableCell>
                  <TableCell>
                    <Badge className="border-0 bg-status-review/15 text-cocoa">
                      {row.next_action}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredQueue.length && (
                <TableRow>
                  <TableCell
                    className="py-10 text-center text-wmuted"
                    colSpan={6}
                    data-testid="reconciliation-action-queue-empty"
                  >
                    {queue.length
                      ? "Tidak ada exception yang sesuai filter."
                      : "Tidak ada exception pada batch terbaru."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="divide-y divide-taupe/20 lg:hidden">
          {pageRows.map((row) => (
            <div
              className="space-y-2 py-4 first:pt-0"
              data-testid={`reconciliation-action-queue-mobile-row-${row.id}`}
              key={row.id}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-wmuted">Order</div>
                  <div className="font-mono text-sm font-semibold text-cocoa">
                    {row.order_id}
                  </div>
                </div>
                <Badge className="border-0 bg-status-review/15 text-cocoa">
                  {row.next_action}
                </Badge>
              </div>
              <div>
                <div className="text-xs text-wmuted">Penyebab</div>
                <div className="text-sm text-cocoa">{row.reason}</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-xs text-wmuted">Qty</div>
                  <div className="font-medium text-cocoa">{row.qty}</div>
                </div>
                <div>
                  <div className="text-xs text-wmuted">Nominal</div>
                  <div className="font-medium text-cocoa">{fmtIDR(row.amount)}</div>
                </div>
                <div>
                  <div className="text-xs text-wmuted">PIC</div>
                  <div className="font-medium text-cocoa">{row.pic}</div>
                </div>
              </div>
            </div>
          ))}
          {!filteredQueue.length && (
            <div
              className="py-10 text-center text-wmuted"
              data-testid="reconciliation-action-queue-mobile-empty"
            >
              {queue.length
                ? "Tidak ada exception yang sesuai filter."
                : "Tidak ada exception pada batch terbaru."}
            </div>
          )}
        </div>
        {filteredQueue.length > 0 && (
          <div
            className="mt-4 flex flex-wrap items-center justify-between gap-3"
            data-testid="reconciliation-action-queue-pagination"
          >
            <p
              className="text-xs text-wmuted"
              data-testid="reconciliation-action-queue-pagination-info"
            >
              Halaman {currentPage} dari {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                data-testid="reconciliation-action-queue-previous-page"
                disabled={currentPage <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                size="sm"
                variant="outline"
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Sebelumnya
              </Button>
              <Button
                data-testid="reconciliation-action-queue-next-page"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                size="sm"
                variant="outline"
              >
                Berikutnya
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </>
  );
}

function FlashHub({ data }) {
  return (
    <Panel className="p-5">
      <div className="text-sm text-wmuted">Paket Kilat terhubung</div>
      <div className="mt-1 text-3xl font-bold text-cocoa">
        {data.summary.linked_flash_orders || 0}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/finance/shipping-reconciliation">
          <Button data-testid="flash-hub-open-shipping">
            Cari Paket Kilat di Crosscheck
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </Link>
        <Link to="/sales">
          <Button data-testid="flash-hub-open-sales" variant="outline">
            Lihat status Sales
          </Button>
        </Link>
      </div>
    </Panel>
  );
}

function MarketplaceCashPanel() {
  return (
    <>
      <div className="mb-4 flex justify-end">
        <Link to="/finance/bca-reconciliation">
          <Button data-testid="control-center-open-bca" variant="outline">
            <Landmark className="mr-1.5 h-4 w-4" />
            Rekonsiliasi BCA
          </Button>
        </Link>
      </div>
      <MarketplaceSettlement />
    </>
  );
}